import { ChevronLeft, ChevronRight, Expand } from 'lucide-react';
import { useState } from 'react';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { ImageViewer } from '~/components/common/image-viewer/image-viewer';
import { Button } from '~/components/ui/button';
import { cn } from '~/utils/cn';

/**
 * Product image carousel with thumbnails and a full-screen viewer.
 *
 * Extracted from the catalog-operations product detail page so the enrichment
 * sample modal shows the same gallery rather than a second implementation of
 * paging, zoom and thumbnail behaviour.
 */
export function ProductImageGallery({
  images,
  title,
  testId = 'product-image-gallery',
}: {
  images: string[];
  title?: string | undefined;
  /** `data-testid` root; derives `-open-viewer-button`, `-prev-button`, `-next-button`, `-thumbnail-{n}`, `-viewer`. */
  testId?: string;
}) {
  const [selected, setSelected] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const hasImages = images.length > 0;

  function prev() {
    setSelected((i) => Math.max(0, i - 1));
  }
  function next() {
    setSelected((i) => Math.min(images.length - 1, i + 1));
  }

  return (
    <div data-testid={testId} className="flex flex-col gap-3">
      {/* Main image — click (or the corner affordance) opens the full viewer. */}
      <div
        data-testid={`${testId}-main`}
        className="group relative aspect-square w-full overflow-hidden rounded-xl bg-neutral-100"
      >
        {hasImages ? (
          <div
            className="flex h-full transition-transform duration-300 ease-in-out"
            style={{ transform: `translateX(-${String(selected * 100)}%)` }}
          >
            {images.map((src, i) => (
              <CachedImage
                key={i}
                src={src}
                alt={`View ${String(i + 1)}`}
                className="h-full w-full shrink-0"
              />
            ))}
          </div>
        ) : (
          <CachedImage src={null} alt="" className="h-full w-full" />
        )}
        {hasImages && (
          <>
            {/* Full-bleed click target, behind the arrows so paging still works. */}
            <Button
              variant="ghost"
              onClick={() => {
                setIsViewerOpen(true);
              }}
              aria-label="Open image viewer"
              data-testid={`${testId}-open-viewer-button`}
              title="Click to zoom"
              className="absolute inset-0 z-0 h-full w-full cursor-zoom-in rounded-none bg-transparent p-0 hover:bg-transparent"
            />
            <span className="pointer-events-none absolute top-3 right-3 z-10 rounded-full bg-white/90 p-2 opacity-0 shadow-sm transition-opacity group-hover:opacity-100">
              <Expand className="h-4 w-4" />
            </span>
          </>
        )}
        {hasImages && images.length > 1 && (
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={prev}
              disabled={selected === 0}
              aria-label="Previous image"
              data-testid={`${testId}-prev-button`}
              className="text-foreground absolute top-1/2 left-3 z-10 size-8 -translate-y-1/2 cursor-pointer rounded-full border-0 bg-white/90 shadow-sm hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="icon"
              onClick={next}
              disabled={selected === images.length - 1}
              aria-label="Next image"
              data-testid={`${testId}-next-button`}
              className="text-foreground absolute top-1/2 right-3 z-10 size-8 -translate-y-1/2 cursor-pointer rounded-full border-0 bg-white/90 shadow-sm hover:bg-white disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </>
        )}
      </div>
      {/* Thumbnails */}
      {hasImages && images.length > 1 && (
        <div data-testid={`${testId}-thumbnails`} className="flex gap-2 overflow-x-auto pb-1">
          {images.map((src, i) => (
            <Button
              key={i}
              variant="ghost"
              size="icon"
              onClick={() => {
                setSelected(i);
              }}
              aria-label={`View image ${String(i + 1)}`}
              data-testid={`${testId}-thumbnail-${String(i)}`}
              aria-current={i === selected}
              className={cn(
                'size-14 shrink-0 cursor-pointer overflow-hidden rounded-lg border-2 p-0 transition-colors',
                i === selected ? 'border-primary' : 'border-transparent hover:border-neutral-300',
              )}
            >
              <CachedImage src={src} alt={`Thumbnail ${String(i + 1)}`} className="h-full w-full" />
            </Button>
          ))}
        </div>
      )}

      <ImageViewer
        images={images}
        index={selected}
        onIndexChange={setSelected}
        open={isViewerOpen}
        onOpenChange={setIsViewerOpen}
        title={title}
        testId={`${testId}-viewer`}
      />
    </div>
  );
}
